/**
 * Axios Interceptors & API Normalization
 * ============================================================================
 *
 * Purpose:
 * --------
 * Centralizes request/response interceptor behavior for the API client:
 * - Adds tracing and client metadata headers to every request
 * - Attaches authentication tokens when available
 * - Normalizes the backend response envelope into a predictable shape
 * - Transforms transport errors into stable domain errors for the UI
 * - Handles session recovery via refresh-token flow on 401 responses
 *
 * Design principles:
 * ------------------
 * - Interceptors must be deterministic and side-effect aware.
 * - UI code should never handle raw Axios errors or unwrap response envelopes.
 * - Authentication retry logic must be protected against infinite loops.
 * - Observability (request IDs, timestamps, versioning) is always included.
 *
 * Non-goals:
 * ----------
 * - Feature/business logic
 * - UI rendering or navigation decisions (except hard redirect on auth failure)
 * - Data transformation beyond envelope normalization
 *
 * Operational notes:
 * ------------------
 * - In DEV: logs requests/responses to aid debugging.
 * - In PROD: forwards errors to monitoring via a single reporting path.
 * - On refresh failure: clears tokens and redirects to `/login` to ensure a
 *   consistent authentication state.
 *
 * Changes to this file affect networking behavior and error handling across
 * the entire application and should be reviewed carefully.
 * ============================================================================
 */

import type { ApiResponse, RefreshTokenResponse } from './types';
import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  clearAuthTokens,
  generateRequestId,
  getAuthToken,
  getRefreshToken,
  logErrorToMonitoring,
  setAuthTokens,
  showErrorNotification,
  transformError,
} from './utils';

import { API_CONFIG } from './constants';
import { API_ENDPOINTS } from './endpoints';
import { apiClient } from './config';

/**
 * Handle successful API response
 *
 * Extracts data from standard backend response format.
 */
export const handleApiResponse = <T = unknown>(
  response: AxiosResponse<ApiResponse<T>>
): AxiosResponse<T> => {
  const { data } = response;

  if (data && typeof data === 'object' && 'success' in data) {
    const result = { ...response, data: data.data as T };

    if (data.meta) {
      (result as unknown as { meta: unknown }).meta = data.meta;
    }

    return result as AxiosResponse<T>;
  }

  return response as AxiosResponse<T>;
};

/**
 * Handle API error
 *
 * Transforms error, shows notification, logs in dev/prod.
 */
export const handleApiError = (error: AxiosError | Error): Promise<never> => {
  const apiError = transformError(error);

  showErrorNotification(apiError);

  if (import.meta.env.DEV) {
    console.error('❌ API Error:', apiError);
  }

  if (import.meta.env.PROD) {
    logErrorToMonitoring(apiError);
  }

  return Promise.reject(apiError instanceof Error ? apiError : new Error(apiError.message));
};

/**
 * Request interceptor - adds auth and tracing headers
 */
export const handleOnRequestSuccess = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const modifiedConfig = { ...config };
  modifiedConfig.headers['X-Request-ID'] = generateRequestId();

  const token = getAuthToken();
  if (token) {
    modifiedConfig.headers.Authorization = `Bearer ${token}`;
  }

  modifiedConfig.headers['X-Client-Timestamp'] = new Date().toISOString();
  modifiedConfig.headers['X-Client-Version'] =
    (import.meta.env.VITE_APP_VERSION as string) ?? '1.0.0';

  if (import.meta.env.DEV) {
    console.warn('📤 API Request:', {
      method: modifiedConfig.method?.toUpperCase(),
      url: modifiedConfig.url,
      baseURL: modifiedConfig.baseURL,
      headers: modifiedConfig.headers,
      data: modifiedConfig.data as unknown,
      params: modifiedConfig.params as unknown,
    });
  }

  return modifiedConfig;
};

export const handleOnRequestError = (error: unknown): Promise<never> => {
  console.error('❌ Request Configuration Error:', error);
  return Promise.reject(error instanceof Error ? error : new Error(String(error)));
};

/**
 * Response interceptor - handles success and errors
 */
export const handleOnResponseSuccess = (response: AxiosResponse): AxiosResponse => {
  if (import.meta.env.DEV) {
    console.warn('📥 API Response:', {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      data: response.data as unknown,
    });
  }

  return handleApiResponse(response as AxiosResponse<ApiResponse>);
};

/**
 * Response error interceptor - handles token refresh
 *
 * On 401:
 * - If refresh token exists: attempts refresh and retries request
 * - If no tokens exist: user was never logged in, don't redirect
 * - If refresh fails: session expired, redirect to login
 */
export const handleOnResponseError = async (error: unknown): Promise<unknown> => {
  const originalRequest = (error as AxiosError)?.config as
    | (InternalAxiosRequestConfig & { _retry?: boolean; _skipAuthRedirect?: boolean })
    | undefined;

  if (
    (error as { response?: { status?: number } }).response?.status === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true;

    const refreshToken = getRefreshToken();
    const accessToken = getAuthToken();

    // If we have a refresh token, try to refresh
    if (refreshToken) {
      try {
        const response = await axios.post<ApiResponse<RefreshTokenResponse> | RefreshTokenResponse>(
          `${API_CONFIG.baseURL}${API_ENDPOINTS.AUTH.REFRESH}`,
          {
            refreshToken,
          }
        );

        const tokenData = 'data' in response.data ? response.data.data : response.data;
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = tokenData as RefreshTokenResponse;

        setAuthTokens(newAccessToken, newRefreshToken);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - session expired, redirect to login
        clearAuthTokens();

        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }

        return Promise.reject(
          refreshError instanceof Error ? refreshError : new Error(String(refreshError))
        );
      }
    } else if (accessToken) {
      // Had an access token but no refresh token - weird state, clear and redirect
      clearAuthTokens();

      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    // If no tokens at all, user was never logged in
    // Don't redirect - let the component handle this (show login prompt, etc.)
    // The error will propagate and can be caught by the calling code
  }

  return handleApiError(error as AxiosError | Error);
};
